import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getKycDocuments,
  uploadKycFile,
  applyKyc,
} from "../../../services/panel/panelServices";
import KycStatusPill from "../../component/KycStatusPill";
import toast from "../../../utils/toast";

const ALLOWED_EXT = ["jpeg", "jpg", "png", "gif", "pdf"];

// Document verifyStatus: 0 Not uploaded, 1 Under screening, 2 Verified, 3 Rejected
const DOC_STATUS = {
  0: { text: "NOT UPLOADED", pill: "pill-neutral" },
  1: { text: "UNDER SCREENING", pill: "pill-pending" },
  2: { text: "VERIFIED", pill: "pill-success" },
  3: { text: "REJECTED", pill: "pill-failed" },
};

// Bottom button per kycStatus; `action: false` → grey, no action
const APPLY_BUTTON = {
  1: { text: "Apply for KYC", action: true },
  2: { text: "KYC APPLIED", action: false },
  3: { text: "KYC COMPLETED", action: false },
  4: { text: "Apply for REKYC", action: true },
  5: { text: "KYC rejected REAPPLY", action: true },
};

// Upload refuses until the profile has PAN, Aadhaar and address
const isProfileIncompleteMsg = (msg = "") => /^First update/i.test(msg);

export default function KycDocuments() {
  const navigate = useNavigate();
  const [isKycData, setKycData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [applying, setApplying] = useState(false);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  // Picked files per row, shown as previews: { [docTypeID]: [{ id, file, url }] }
  const [selectedFiles, setSelectedFiles] = useState({});
  const fileInputRef = useRef(null);
  const uploadRowRef = useRef(null);
  const selectedFilesRef = useRef(selectedFiles);
  selectedFilesRef.current = selectedFiles;

  useEffect(() => {
    fetchKycDocuments();
    // Free the preview blob URLs when leaving the page
    return () =>
      Object.values(selectedFilesRef.current)
        .flat()
        .forEach((f) => URL.revokeObjectURL(f.url));
  }, []);

  const fetchKycDocuments = async () => {
    setLoading(true);
    try {
      const res = await getKycDocuments();

      if (res?.statuscode === 1) {
        setKycData(res);
      } else {
        toast.error(res?.msg || "Could not load the KYC documents.");
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch KYC documents",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePickFile = (doc) => {
    uploadRowRef.current = doc;
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    const doc = uploadRowRef.current;
    if (!file || !doc) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error("Only .jpeg, .jpg, .png, .gif or .pdf files are allowed.");
      return;
    }
    if (!file.size) {
      toast.error("Empty file not allowed.");
      return;
    }

    // One file per document — a new pick replaces the old preview
    clearPicked(doc.docTypeID);
    setSelectedFiles((prev) => ({
      ...prev,
      [doc.docTypeID]: [
        {
          id: `${file.name}-${file.size}-${file.lastModified}`,
          file,
          url: URL.createObjectURL(file),
        },
      ],
    }));
  };

  const handleRemoveFile = (docTypeID, id) => {
    setSelectedFiles((prev) => {
      const list = prev[docTypeID] || [];
      const removed = list.find((f) => f.id === id);
      if (removed) URL.revokeObjectURL(removed.url);
      return { ...prev, [docTypeID]: list.filter((f) => f.id !== id) };
    });
  };

  const clearPicked = (docTypeID) =>
    setSelectedFiles((prev) => {
      (prev[docTypeID] || []).forEach((f) => URL.revokeObjectURL(f.url));
      return { ...prev, [docTypeID]: [] };
    });

  // Each document uploads on its own with the files picked for that row
  const handleUpload = async (doc) => {
    const picked = selectedFiles[doc.docTypeID] || [];
    if (!picked.length) return;
    setUploadingId(doc.docTypeID);
    try {
      const res = await uploadKycFile(doc.docTypeID, picked[0].file);
      if (res?.statuscode === 1) {
        toast.success(res?.msg || `${doc.docName} uploaded.`);
        setProfileIncomplete(false);
        clearPicked(doc.docTypeID);
        fetchKycDocuments();
      } else {
        toast.error(res?.msg || "Failed to upload document.");
        setProfileIncomplete(isProfileIncompleteMsg(res?.msg));
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to upload document.");
    } finally {
      setUploadingId(null);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await applyKyc();
      if (res?.statuscode === 1) {
        toast.success(res?.msg || "Applied for KYC.");
      } else {
        toast.error(res?.msg || "Could not apply for KYC.");
      }
      fetchKycDocuments(); // status or required docs may have changed either way
    } catch (err) {
      toast.error(err.response?.data?.msg || "Could not apply for KYC.");
    } finally {
      setApplying(false);
    }
  };

  const documents = isKycData?.documents || [];
  const pendingRequired = isKycData?.pendingRequired || 0;
  const missingRequired = documents.filter(
    (d) => d.isRequired && !d.isUploaded,
  );
  const applyBtn = APPLY_BUTTON[isKycData?.kycStatus];
  const canApply =
    applyBtn?.action && isKycData?.canApply && pendingRequired === 0;

  return (
    <div className="screen active" id="screen-kyc">
      <div className="page-head">
        <div>
          <h1>KYC documents</h1>
          <div className="ph-sub">
            Upload the required documents, then apply for KYC · the admin
            reviews and approves
          </div>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate("/company-profile")}
        >
          ‹ Back to profile
        </button>
      </div>

      {profileIncomplete && (
        <div
          className="card"
          style={{
            marginBottom: 18,
            borderColor: "var(--amber)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span>
            Your profile needs a valid PAN, Aadhaar and address before you can
            upload documents.
          </span>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate("/company-profile")}
          >
            Update profile
          </button>
        </div>
      )}

      <div className="card">
        <div className="card-head-row">
          <div>
            <div className="card-title">Documents</div>
            <div className="card-sub">
              {pendingRequired > 0
                ? `${pendingRequired} required document(s) still to upload`
                : "All required documents are uploaded"}
            </div>
          </div>
          <KycStatusPill
            status={isKycData?.kycStatus}
            text={isKycData?.kycStatusText}
          />
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <tbody>
              <tr>
                <th>Document</th>
                <th>Uploaded file</th>
                <th>Preview</th>
                <th>Status</th>
              </tr>
              {loading && !documents.length ? (
                <tr>
                  <td colSpan={4} className="empty-note">
                    Loading documents...
                  </td>
                </tr>
              ) : documents.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-note">
                    No documents found
                  </td>
                </tr>
              ) : (
                documents.map((doc) => {
                  const status = DOC_STATUS[doc.verifyStatus];
                  const uploading = uploadingId === doc.docTypeID;
                  const picked = selectedFiles[doc.docTypeID] || [];
                  return (
                    <tr key={doc.docTypeID}>
                      <td title={doc.requirement || undefined}>
                        <div style={{ fontWeight: 600 }}>
                          {doc.docName}
                          {doc.isRequired && (
                            <span style={{ color: "#dc2626" }}> *</span>
                          )}
                        </div>
                        {doc.requirement && (
                          <div
                            style={{ fontSize: 11, color: "var(--text-faint)" }}
                          >
                            {doc.requirement}
                          </div>
                        )}
                      </td>
                      <td>
                        {/* No view/download API — only show that it exists */}
                        {doc.canUpload ? (
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            {picked.length === 0 && (
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => handlePickFile(doc)}
                                disabled={uploadingId !== null || applying}
                              >
                                {doc.isUploaded ? "Change" : "Select file"}
                              </button>
                            )}
                            {picked.length > 0 && (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleUpload(doc)}
                                disabled={uploadingId !== null || applying}
                              >
                                {uploading ? "Uploading..." : "Upload"}
                              </button>
                            )}
                          </div>
                        ) : doc.isUploaded ? (
                          <span style={{ color: "var(--text-faint)" }}>
                            Uploaded
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {picked.length === 0 ? (
                          <span style={{ color: "var(--text-faint)" }}>—</span>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                            }}
                          >
                            {picked.map((f) => (
                              <div
                                key={f.id}
                                title={f.file.name}
                                style={{
                                  position: "relative",
                                  width: 56,
                                  height: 56,
                                  borderRadius: 8,
                                  border:
                                    "1px solid var(--hair-2, rgba(14,38,72,.18))",
                                  background: "var(--surface-2, #f6f8fb)",
                                  overflow: "hidden",
                                  flexShrink: 0,
                                }}
                              >
                                {/* Click opens the full file in a new tab */}
                                <a
                                  href={f.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                    height: "100%",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: "var(--red, #d93954)",
                                    textDecoration: "none",
                                  }}
                                >
                                  {f.file.type.startsWith("image/") ? (
                                    <img
                                      src={f.url}
                                      alt={f.file.name}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    "PDF"
                                  )}
                                </a>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleRemoveFile(doc.docTypeID, f.id)
                                  }
                                  aria-label={`Remove ${f.file.name}`}
                                  style={{
                                    position: "absolute",
                                    top: 2,
                                    right: 2,
                                    width: 18,
                                    height: 18,
                                    padding: 0,
                                    border: "none",
                                    borderRadius: "50%",
                                    background: "rgba(15,31,56,.7)",
                                    color: "#fff",
                                    fontSize: 11,
                                    lineHeight: "18px",
                                    cursor: "pointer",
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          className={`pill ${status?.pill || "pill-neutral"}`}
                        >
                          {doc.verifyStatusText || status?.text || "—"}
                        </span>
                        {doc.verifyStatus === 3 && doc.rejectReason && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#dc2626",
                              marginTop: 4,
                            }}
                          >
                            {doc.rejectReason}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".jpeg,.jpg,.png,.gif,.pdf"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            marginTop: 14,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 11.5, color: "var(--text-faint)" }}>
            Only .jpeg, .jpg, .png, .gif, .pdf · images over 200 KB are shrunk ·{" "}
            <span style={{ color: "#dc2626" }}>*</span> required for KYC
            {applyBtn?.action && missingRequired.length > 0 && (
              <div style={{ color: "var(--amber)", marginTop: 4 }}>
                Missing: {missingRequired.map((d) => d.docName).join(", ")}
              </div>
            )}
          </div>
          {applyBtn && (
            <button
              className={`btn ${applyBtn.action ? "btn-primary" : "btn-ghost"}`}
              onClick={handleApply}
              disabled={!canApply || applying || uploadingId !== null}
            >
              {applying ? "Applying..." : applyBtn.text}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
